/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'

import { EuiButton, EuiFlexGroup, EuiFlexItem } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type { AnyProject } from '@modules/project-api/types'
import type { ProjectID, ProjectType } from '@modules/ui-types/projects'

import SelectProjectsStep from './SelectProjectsStep'

type Props = {
  projectId: ProjectID
  projectType: ProjectType
  linkedProjectIds: ProjectID[]
  selectedProjects: AnyProject[]
  onSelectionChange: (projects: AnyProject[]) => void
  hasSelection: boolean
  hasReachedMaxLinkedProjects: boolean
  onCancel: () => void
  onReviewAndSave: () => void
}

const SelectProjectsStepContainer: React.FunctionComponent<Props> = ({
  projectId,
  projectType,
  linkedProjectIds,
  selectedProjects,
  onSelectionChange,
  hasSelection,
  hasReachedMaxLinkedProjects,
  onCancel,
  onReviewAndSave,
}) => (
  <EuiFlexGroup direction='column' gutterSize='m' alignItems='stretch'>
    <EuiFlexItem grow={false} css={{ maxWidth: '60rem' }}>
      <SelectProjectsStep
        currentProjectId={projectId}
        currentProjectType={projectType}
        linkedProjectIds={linkedProjectIds}
        selectedProjects={selectedProjects}
        onSelectionChange={onSelectionChange}
      />
    </EuiFlexItem>

    <EuiFlexItem>
      <EuiFlexGroup justifyContent='flexEnd' gutterSize='m'>
        <EuiFlexItem grow={false}>
          <EuiButton onClick={onCancel} data-test-subj='cps-selectProjectsStep-cancelButton'>
            <FormattedMessage id='cps.link.cancel' defaultMessage='Cancel' />
          </EuiButton>
        </EuiFlexItem>
        <EuiFlexItem grow={false}>
          <EuiButton
            fill={true}
            onClick={onReviewAndSave}
            disabled={!hasSelection || hasReachedMaxLinkedProjects}
            data-test-subj='cps-selectProjectsStep-reviewAndSaveButton'
          >
            <FormattedMessage id='cps.link.reviewAndSave' defaultMessage='Review and save' />
          </EuiButton>
        </EuiFlexItem>
      </EuiFlexGroup>
    </EuiFlexItem>
  </EuiFlexGroup>
)

export default SelectProjectsStepContainer
